import React, { useState, ReactNode, useEffect, useRef } from "react";
import cn from "classnames";
import { APP_EMITTER_EVENTS, AppEmitter, scrollToElementFromParent } from "../../lib";
import { ScrollableListItem } from "./scrollable-list-item/scrollable-list-item";
import styles from "./scrollable-list.module.scss";

export interface IScrollableListItem {
    value: string | number;
    name: string;
}

interface IScrollableListProps {
    className?: string;
    name: string;
    actionButton?: ReactNode;
    onActionButtonClick?: (value: IScrollableListItem["value"]) => void;
    onSelectedItemChange?: (value: IScrollableListItem["value"]) => void;
    value?: string | number;
    items: IScrollableListItem[];
}

export const ScrollableList: React.FC<IScrollableListProps> = React.memo(({
                                                                              className,
                                                                              name,
                                                                              actionButton,
                                                                              value,
                                                                              onActionButtonClick,
                                                                              onSelectedItemChange,
                                                                              items
                                                                          }) => {
    const [selectedItem, setSelectedItem] = useState<IScrollableListItem["value"] | undefined>(value);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        setSelectedItem(value);
    }, [value]);

    useEffect(() => {
        if (containerRef.current && selectedItem) {
            const element = containerRef.current.querySelector(`#${getItemId(selectedItem)}`);
            if (element) {
                AppEmitter.emit(APP_EMITTER_EVENTS.blockLayoutScroll);
                setTimeout(() => {
                    element.scrollIntoView({ behavior: "smooth", block: "center" });
                }, 100);
            }
        }
    }, [selectedItem, containerRef.current]);

    const getItemId = (value: string | number) => {
        return `scrollable_list_${name}_${value}`;
    };

    const onItemSelect = (value: IScrollableListItem["value"]) => {
        setSelectedItem(value);
        onSelectedItemChange && onSelectedItemChange(value);
    };

    return <div className={cn(styles.container, className)} ref={containerRef}>
        {items.map((item) => <ScrollableListItem item={item}
                                                 id={getItemId(item.value)}
                                                 key={item.value}
                                                 onItemSelect={onItemSelect}
                                                 selectedItem={selectedItem}
                                                 onActionButtonClick={onActionButtonClick}
                                                 actionButton={actionButton}/>)}
    </div>;
});
